<template>
    <div class="arv-dashboard">
        <div class="arv-dashboard__header">
            <div>
                <h1>{{ $t('title') }}</h1>
                <p class="t-subtle">
                    {{ $t('subtitle', { count: driverRows.length }) }}
                </p>
            </div>

            <div class="arv-dashboard__controls">
                <SegmentButton v-model="days" :options="timeDurationOptions" />
            </div>
        </div>

        <div v-if="isEcoscoreArvLoading" class="l-padded l-inline l-center">
            <VSpinner size="medium" :line-fg-color="spinnerColor" :speed="1" />
        </div>

        <div v-else class="l-padded l-stack l-gap-2">
            <div class="arv-dashboard__violation-cards">
                <div
                    v-for="category in violationCategories"
                    :key="category.label"
                    class="arv-dashboard__violation-card"
                    :class="{
                        'arv-dashboard__violation-card--alert': category.count,
                    }"
                >
                    <p class="arv-dashboard__violation-label">
                        {{ category.label }}
                    </p>
                    <p
                        class="arv-dashboard__violation-count"
                        :class="{
                            'arv-dashboard__violation-count--alert':
                                category.count,
                        }"
                    >
                        {{ category.count }}
                    </p>
                    <p class="arv-dashboard__violation-label">
                        {{
                            category.count
                                ? $t('driversAffected')
                                : $t('noViolations')
                        }}
                    </p>
                </div>
            </div>

            <div v-if="violators.length" class="arv-dashboard__warning">
                <div class="arv-dashboard__warning-icon">
                    <WarningOutlinedIcon width="1em" height="1em" />
                </div>
                <div>
                    <p class="arv-dashboard__warning-title">
                        {{ $t('flaggedDrivers', { count: violators.length }) }}
                    </p>
                    <p class="arv-dashboard__warning-desc">
                        {{ $t('limitsDescription') }}
                    </p>
                    <div class="arv-dashboard__warning-tags">
                        <span
                            v-for="driver in violators.slice(0, 8)"
                            :key="driver.id"
                            class="arv-dashboard__warning-tag"
                        >
                            {{ driver.name }}
                        </span>
                        <span
                            v-if="violators.length > 8"
                            class="arv-dashboard__warning-more"
                        >
                            +{{ violators.length - 8 }}
                            {{ $t('more') }}
                        </span>
                    </div>
                </div>
            </div>

            <div class="arv-dashboard__chart-card">
                <p class="arv-dashboard__chart-title">
                    {{ $t('monthlyTrend') }}
                </p>
                <p class="arv-dashboard__chart-subtitle">
                    {{ $t('monthlyTrendDesc') }}
                </p>
                <ApexChart
                    height="260"
                    :options="chartOptions"
                    :series="chartSeries"
                />
            </div>

            <EcoScoreArvTable :drivers="driverRows" />
        </div>
    </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex'
import ApexChart from 'vue-apexcharts'
import VSpinner from 'vue-simple-spinner'

import {
    MAX_AVG_WEEKLY_WORKING,
    MAX_BIWEEKLY_DRIVING,
    MAX_WEEKLY_DRIVING,
    MAX_WEEKLY_WORKING,
} from '@/store/modules/ecoscore'
import EcoScoreArvTable from '@/components/ecoscore/EcoScoreArvTable'
import SegmentButton from '@/components/redesigned/SegmentButton'
import WarningOutlinedIcon from '@/components/icons/WarningOutlinedIcon'

export default {
    name: 'EcoScoreArvView',
    components: {
        ApexChart,
        EcoScoreArvTable,
        SegmentButton,
        VSpinner,
        WarningOutlinedIcon,
    },
    data() {
        return {
            days: [7],
            spinnerColor: process.env.VUE_APP_COLOR_PRIMARY,
            timeDurationOptions: [
                { value: 7, label: this.$tc('days', 7) },
                { value: 14, label: this.$tc('days', 14) },
                { value: 28, label: this.$tc('days', 28) },
            ],
        }
    },
    computed: {
        ...mapState('ecoscore', ['isEcoscoreArvLoading']),
        ...mapGetters('ecoscore', [
            'ecoscoreArvByDays',
            'ecoscoreArvMonthlyData',
        ]),
        driverRows() {
            return this.ecoscoreArvByDays(this.days[0])
        },
        violators() {
            return this.driverRows.filter(driver => driver.hasViolation)
        },
        violationCategories() {
            return [
                {
                    label: this.$t('avgWorkExceeded'),
                    count: this.driverRows.filter(
                        driver =>
                            driver.avgWeeklyWorking > MAX_AVG_WEEKLY_WORKING
                    ).length,
                },
                {
                    label: this.$t('maxWorkExceeded'),
                    count: this.driverRows.filter(
                        driver => driver.maxWeeklyWorking > MAX_WEEKLY_WORKING
                    ).length,
                },
                {
                    label: this.$t('maxDriveExceeded'),
                    count: this.driverRows.filter(
                        driver => driver.maxWeeklyDriving > MAX_WEEKLY_DRIVING
                    ).length,
                },
                {
                    label: this.$t('maxDriveBiweekExceeded'),
                    count: this.driverRows.filter(
                        driver =>
                            driver.maxBiweeklyDriving > MAX_BIWEEKLY_DRIVING
                    ).length,
                },
            ]
        },
        chartOptions() {
            return {
                chart: {
                    type: 'bar',
                    toolbar: { show: false },
                },
                colors: ['#2e73b8', '#cc8a00'],
                dataLabels: { enabled: false },
                xaxis: {
                    categories: this.ecoscoreArvMonthlyData.map(
                        item => item.label
                    ),
                },
                yaxis: {
                    labels: {
                        formatter: value => `${value}h`,
                    },
                },
            }
        },
        chartSeries() {
            return [
                {
                    name: this.$t('drivingTime'),
                    data: this.ecoscoreArvMonthlyData.map(m => m.driving),
                },
                {
                    name: this.$t('workingTimeInclDriving'),
                    data: this.ecoscoreArvMonthlyData.map(m => m.working),
                },
            ]
        },
    },
    mounted() {
        this.loadEcoscoreArv()
    },
    methods: {
        ...mapActions('ecoscore', ['loadEcoscoreArv']),
    },
}
</script>

<i18n>
{
    "en": {
        "avgWorkExceeded": "Avg work >48h/wk",
        "days": "{n} Day | {n} Days",
        "driversAffected": "drivers affected",
        "drivingTime": "Driving time",
        "flaggedDrivers": "Flagged drivers ({count})",
        "limitsDescription": "Avg working time max 48h/wk (26 wk) · Max 60h/wk · Driving time max 56h/wk · Max 90h/2 wk",
        "maxDriveBiweekExceeded": "Drive >90h/2wk",
        "maxDriveExceeded": "Drive >56h/wk",
        "maxWorkExceeded": "Work >60h/wk",
        "monthlyTrend": "Monthly trend – working & driving times",
        "monthlyTrendDesc": "Fleet total of the last 6 months in hours",
        "more": "more",
        "noViolations": "✓ no violations",
        "subtitle": "Driving, rest and working times · {count} drivers",
        "title": "Tachograph Data",
        "workingTimeInclDriving": "Working time (incl. driving)"
    },
    "de": {
        "avgWorkExceeded": "Ø Arbeit >48h/Wo",
        "days": "{n} Tag | {n} Tage",
        "driversAffected": "Fahrer betroffen",
        "drivingTime": "Lenkzeit",
        "flaggedDrivers": "Auffällige Fahrer ({count})",
        "limitsDescription": "Ø Arbeitszeit max. 48h/Wo (26 Wo.) · Max. 60h/Wo · Lenkzeit max. 56h/Wo · Max. 90h/2 Wo.",
        "maxDriveBiweekExceeded": "Lenkzeit >90h/2Wo",
        "maxDriveExceeded": "Lenkzeit >56h/Wo",
        "maxWorkExceeded": "Arbeit >60h/Wo",
        "monthlyTrend": "Monatlicher Verlauf – Arbeits- & Lenkzeiten",
        "monthlyTrendDesc": "Flottentotal der letzten 6 Monate in Stunden",
        "more": "weitere",
        "noViolations": "✓ keine Verstösse",
        "subtitle": "Lenk-, Ruhe- und Arbeitszeiten · {count} Fahrer",
        "title": "Tachograph-Daten",
        "workingTimeInclDriving": "Arbeitszeit (inkl. Lenk)"
    },
    "fr": {
        "avgWorkExceeded": "Moy. travail >48h/sem",
        "days": "{n} Jour | {n} Jours",
        "driversAffected": "conducteurs concernés",
        "drivingTime": "Temps de conduite",
        "flaggedDrivers": "Conducteurs signalés ({count})",
        "limitsDescription": "Moy. travail max 48h/sem (26 sem.) · Max 60h/sem · Conduite max 56h/sem · Max 90h/2 sem.",
        "maxDriveBiweekExceeded": "Conduite >90h/2sem",
        "maxDriveExceeded": "Conduite >56h/sem",
        "maxWorkExceeded": "Travail >60h/sem",
        "monthlyTrend": "Tendance mensuelle – temps de travail et conduite",
        "monthlyTrendDesc": "Total flotte des 6 derniers mois en heures",
        "more": "de plus",
        "noViolations": "✓ aucune infraction",
        "subtitle": "Temps de conduite, de repos et de travail · {count} conducteurs",
        "title": "Données tachygraphe",
        "workingTimeInclDriving": "Temps de travail (incl. conduite)"
    },
    "it": {
        "avgWorkExceeded": "Media lavoro >48h/sett",
        "days": "{n} Giorno | {n} Giorni",
        "driversAffected": "autisti coinvolti",
        "drivingTime": "Tempo di guida",
        "flaggedDrivers": "Autisti segnalati ({count})",
        "limitsDescription": "Media lavoro max 48h/sett (26 sett.) · Max 60h/sett · Guida max 56h/sett · Max 90h/2 sett.",
        "maxDriveBiweekExceeded": "Guida >90h/2sett",
        "maxDriveExceeded": "Guida >56h/sett",
        "maxWorkExceeded": "Lavoro >60h/sett",
        "monthlyTrend": "Tendenza mensile – tempi di lavoro e guida",
        "monthlyTrendDesc": "Totale flotta degli ultimi 6 mesi in ore",
        "more": "altri",
        "noViolations": "✓ nessuna violazione",
        "subtitle": "Tempi di guida, riposo e lavoro · {count} autisti",
        "title": "Dati tachigrafo",
        "workingTimeInclDriving": "Tempo di lavoro (incl. guida)"
    }
}
</i18n>

<style lang="scss" scoped>
.arv-dashboard {
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 100%;

    &__header {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        justify-content: space-between;
        gap: 1rem;
        padding: 1.5rem 2rem;
        background-color: $color-gray-lighter-new;
        border-bottom: $style-border;

        h1 {
            font-size: 28px;
            color: $color-primary;
        }
    }

    &__controls {
        display: flex;
        align-items: center;
        gap: 0.75rem;

        .segment-button {
            white-space: nowrap;
        }
    }

    &__violation-cards {
        display: grid;
        grid-template-columns: repeat(4, 1fr);
        gap: 0.75rem;

        @include respond-to('for-mobile-down') {
            grid-template-columns: repeat(2, 1fr);
        }
    }

    &__violation-card {
        padding: 1rem;
        border: $style-border;
        border-radius: 0.75rem;
        text-align: center;
        background: #fff;

        &--alert {
            border-color: rgba($color-danger, 0.2);
        }
    }

    &__violation-label {
        font-size: 0.75rem;
        color: $color-gray-darker;
    }

    &__violation-count {
        font-size: 1.5rem;
        font-weight: 700;
        color: $color-text;

        &--alert {
            color: $color-danger;
        }
    }

    &__warning {
        display: flex;
        align-items: flex-start;
        gap: 0.75rem;
        padding: 1.25rem;
        border: 1px solid rgba($color-danger, 0.2);
        border-radius: 0.75rem;
        background-color: #fdf2f2;
    }

    &__warning-icon {
        font-size: 1.25rem;
        flex-shrink: 0;
    }

    &__warning-title {
        font-size: 0.875rem;
        font-weight: 600;
        color: $color-text;
    }

    &__warning-desc {
        font-size: 0.875rem;
        color: $color-gray-darker;
    }

    &__warning-tags {
        display: flex;
        flex-wrap: wrap;
        gap: 0.5rem;
        margin-top: 0.5rem;
    }

    &__warning-tag {
        display: inline-block;
        padding: 0.125rem 0.625rem;
        border-radius: 999px;
        font-size: 0.75rem;
        font-weight: 500;
        color: $color-danger;
        background-color: rgba($color-danger, 0.1);
    }

    &__warning-more {
        font-size: 0.75rem;
        color: $color-gray-darker;
        align-self: center;
    }

    &__chart-card {
        padding: 1.25rem;
        border: $style-border;
        border-radius: 0.75rem;
        background: #fff;
    }

    &__chart-title {
        font-size: 0.9375rem;
        font-weight: 600;
        color: $color-text;
    }

    &__chart-subtitle {
        font-size: 0.75rem;
        color: $color-gray-darker;
        margin-bottom: 0.5rem;
    }
}
</style>
